import type {
  ManifestModel,
  Feature as ApiManifestFeature,
} from '@/adapters/apiv1.g';
import type { LogoAsset } from '@/types/shared/logo';
import type {
  ProviderManifest,
  ProviderManifestFeature,
} from '@/types/core/provider-manifest';
import type { JSONSchema } from '@/types/shared/json-schema';
import type { UUID } from '@/utils/uuid';
import { isUUID } from '@/utils/uuid';

type LogoInput = { light: string; dark: string };

function extractLogo(value?: Partial<LogoInput>): LogoAsset | undefined {
  const light = value?.light?.trim();
  const dark = value?.dark?.trim();
  if (light && dark) {
    return {
      light,
      dark,
    };
  }

  return undefined;
}

function extractManifestFeature(
  key: string,
  feature: ApiManifestFeature
): ProviderManifestFeature {
  return {
    key,
    featureId: isUUID(feature.feature_id)
      ? (feature.feature_id as UUID)
      : undefined,
    name: feature.name ?? key,
    displayName: feature.display_name ?? undefined,
    description: feature.description ?? undefined,
    requirements: feature.requirements ?? [],
    optionsSchema:
      feature.options_schema && typeof feature.options_schema === 'object'
        ? (feature.options_schema as JSONSchema)
        : undefined,
  };
}

/**
 * Normalize connector manifest catalog entries into provider manifest options.
 */
export function extractConnectorManifest(
  model: ManifestModel
): ProviderManifest | null {
  const manifest = model.manifest;
  if (!manifest) return null;
  const modelWithLogo = model as ManifestModel & {
    logo?: Partial<LogoInput>;
  };
  const manifestWithLogo = manifest as typeof manifest & {
    logo?: Partial<LogoInput>;
  };

  const key =
    manifest.provider_id ?? manifest.name ?? manifest.display_name ?? '';

  if (!key) {
    return null;
  }

  const featureEntries = Object.entries(
    (manifest.features ?? {}) as Record<string, ApiManifestFeature>
  );
  const features = featureEntries.map(([featureKey, feature]) =>
    extractManifestFeature(featureKey, feature)
  );

  // Backend-driven logo extraction:
  // prefer model-level `logo`, then fallback to manifest-level `logo`.
  const logo =
    extractLogo(modelWithLogo.logo) ?? extractLogo(manifestWithLogo.logo);

  return {
    key,
    providerId: isUUID(manifest.provider_id)
      ? (manifest.provider_id as UUID)
      : undefined,
    version: manifest.version ?? undefined,
    name: manifest.name ?? key,
    displayName: manifest.display_name ?? undefined,
    logo,
    description: manifest.description ?? undefined,
    features,
    installedVersion: model.installed_version ?? undefined,
    latestVersion: model.latest_version ?? undefined,
    upgradeAvailable: model.upgrade_available ?? false,
  };
}
